import { useEffect, useMemo, useState, Fragment } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronDown, ChevronRight, Link2, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TablePaginationBar } from '@/components/shared/TablePaginationBar';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';
import { productMasterService } from '@/modules/productMaster/productMasterService';
import {
  equivalencesService,
  type EquivalenceGroup,
  type EquivalenceUnificationPreview,
  type UnifyScope,
} from './equivalencesService';
import { ProductCodePicker, type PickedProduct } from './ProductCodePicker';

const inputClass =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary';
const btnPrimaryClass =
  'inline-flex items-center justify-center rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
const btnSecondaryClass =
  'inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50';
const btnDangerClass =
  'inline-flex items-center justify-center rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 shadow-sm hover:bg-red-50 disabled:opacity-50';
const btnGhostClass =
  'inline-flex items-center gap-1.5 text-sm font-medium text-stocker-primary hover:underline';

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object' && 'message' in e) {
    const msg = (e as { message?: unknown; detail?: unknown }).message;
    const detail = (e as { detail?: unknown }).detail;
    if (typeof msg === 'string' && msg.trim()) return msg;
    if (typeof detail === 'string' && detail.trim()) return detail;
  }
  return String(e);
}

/** Format qty: integers without decimals; else trim trailing zeros. */
export function formatQty(raw: string | number | undefined | null): string {
  if (raw === undefined || raw === null || raw === '') return '—';
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  if (Number.isInteger(n)) return String(n);
  const asNum = Number(n.toFixed(6));
  if (Number.isInteger(asNum)) return String(asNum);
  return String(asNum);
}

/** Factors shown with up to 2 decimal places. */
export function formatFactor(raw: string | number | undefined | null): string {
  if (raw === undefined || raw === null || raw === '') return '';
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  return (Math.round(n * 100) / 100).toFixed(2);
}

export function EquivalencesPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const productHint = searchParams.get('product');
  const [groups, setGroups] = useState<EquivalenceGroup[]>([]);
  const [selected, setSelected] = useState<EquivalenceGroup | null>(null);
  const [principal, setPrincipal] = useState<PickedProduct | null>(null);
  const [aliases, setAliases] = useState<Array<PickedProduct | null>>([null]);
  const [salesFactor, setSalesFactor] = useState('1');
  const [stockFactor, setStockFactor] = useState('1');
  const [salesPreview, setSalesPreview] = useState<EquivalenceUnificationPreview | null>(null);
  const [stockPreview, setStockPreview] = useState<EquivalenceUnificationPreview | null>(null);
  const [expandedSalesDates, setExpandedSalesDates] = useState<Set<string>>(new Set());
  const [salesDatesPageSize, setSalesDatesPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [salesDatesPage, setSalesDatesPage] = useState(1);
  const [groupsPageSize, setGroupsPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [groupsPage, setGroupsPage] = useState(1);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState<UnifyScope | null>(null);
  const [unifying, setUnifying] = useState<UnifyScope | null>(null);
  const [undoing, setUndoing] = useState<UnifyScope | null>(null);
  const [dissolving, setDissolving] = useState<'plain' | 'revert' | null>(null);

  const reload = async () => {
    const rows = await equivalencesService.list();
    setGroups(rows);
    let nextSelected: EquivalenceGroup | null = null;
    setSelected((prev) => {
      if (productHint) {
        const hit = rows.find(
          (g) =>
            String(g.canonical_product_id) === productHint ||
            g.members.some((m) => String(m.equivalent_product_id) === productHint)
        );
        if (hit) {
          nextSelected = hit;
          return hit;
        }
      }
      if (prev) {
        const kept = rows.find((g) => g.canonical_product_id === prev.canonical_product_id) || null;
        nextSelected = kept;
        return kept;
      }
      return prev;
    });
    setGroupsPage((current) => {
      const totalPages = Math.max(1, Math.ceil(rows.length / groupsPageSize));
      const clamped = Math.min(current, totalPages);
      if (!nextSelected) return clamped;
      const idx = rows.findIndex(
        (g) => g.canonical_product_id === nextSelected?.canonical_product_id,
      );
      if (idx < 0) return clamped;
      const start = (clamped - 1) * groupsPageSize;
      if (idx >= start && idx < start + groupsPageSize) return clamped;
      return Math.floor(idx / groupsPageSize) + 1;
    });
    return rows;
  };

  useEffect(() => {
    reload().catch((e) => setError(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productHint]);

  useEffect(() => {
    if (!productHint) return;
    let cancelled = false;
    productMasterService
      .get(Number(productHint))
      .then((sheet) => {
        if (cancelled) return;
        setPrincipal({
          id: sheet.id,
          code: sheet.code,
          name: sheet.name,
          client_code: sheet.client_code,
        });
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
  }, [productHint]);

  const selectedMembers = useMemo(() => {
    const rows = [...(selected?.members || [])];
    rows.sort((a, b) => Number(b.is_active) - Number(a.is_active));
    return rows;
  }, [selected]);
  const principalMember = selectedMembers.find((m) => m.is_active);
  const units = selected?.units || [];

  const salesByDate = useMemo(() => {
    const map = new Map<
      string,
      Array<{
        alias_code: string;
        date: string;
        warehouse_id: number | null;
        qty_alias: string;
        qty_principal_ceiled: string;
        is_principal?: boolean;
      }>
    >();
    for (const row of salesPreview?.sales_preview || []) {
      const list = map.get(row.date) || [];
      list.push(row);
      map.set(row.date, list);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([date, rows]) => {
        const ordered = [...rows].sort((a, b) => {
          if (Boolean(a.is_principal) !== Boolean(b.is_principal)) {
            return a.is_principal ? -1 : 1;
          }
          return String(a.alias_code).localeCompare(String(b.alias_code));
        });
        return {
          date,
          rows: ordered,
          productCount: new Set(ordered.map((r) => r.alias_code)).size,
          // Total del día tras unificar = principal ya en planning + aportes convertidos.
          totalPrincipal: ordered.reduce(
            (sum, r) => sum + (Number(r.qty_principal_ceiled) || 0),
            0,
          ),
        };
      });
  }, [salesPreview]);

  const stockPreviewRows = useMemo(() => {
    const rows = [...(stockPreview?.stock_preview || [])];
    rows.sort((a, b) => {
      if (Boolean(a.is_principal) !== Boolean(b.is_principal)) {
        return a.is_principal ? -1 : 1;
      }
      const wh = String(a.warehouse_id ?? '').localeCompare(String(b.warehouse_id ?? ''));
      if (wh !== 0) return wh;
      return String(a.alias_code).localeCompare(String(b.alias_code));
    });
    const totalAfter = rows.reduce((sum, r) => sum + (Number(r.qty_principal_ceiled) || 0), 0);
    return { rows, totalAfter };
  }, [stockPreview]);

  const groupsTotalPages = Math.max(1, Math.ceil(groups.length / groupsPageSize));
  const groupsPageSafe = Math.min(groupsPage, groupsTotalPages);
  const groupsPageRows = useMemo(() => {
    const start = (groupsPageSafe - 1) * groupsPageSize;
    return groups.slice(start, start + groupsPageSize);
  }, [groups, groupsPageSafe, groupsPageSize]);

  const salesDatesTotalPages = Math.max(1, Math.ceil(salesByDate.length / salesDatesPageSize));
  const salesDatesPageSafe = Math.min(salesDatesPage, salesDatesTotalPages);
  const salesByDatePage = useMemo(() => {
    const start = (salesDatesPageSafe - 1) * salesDatesPageSize;
    return salesByDate.slice(start, start + salesDatesPageSize);
  }, [salesByDate, salesDatesPageSafe, salesDatesPageSize]);

  useEffect(() => {
    setSalesDatesPage(1);
    setExpandedSalesDates(new Set());
  }, [salesPreview, salesDatesPageSize]);

  const salesDone = selected?.sales_unification_status === 'done';
  const stockDone = selected?.stock_unification_status === 'done';
  const bothDone = salesDone && stockDone;

  const toggleSalesDate = (date: string) => {
    setExpandedSalesDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const createGroup = async () => {
    const validAliases = aliases.filter((a): a is PickedProduct => Boolean(a?.id));
    if (!principal || validAliases.length === 0) {
      setError(t('equivalences.selectBothProducts'));
      return;
    }
    setError(null);
    setMessage(null);
    setSaving(true);
    const errors: string[] = [];
    let ok = 0;
    try {
      for (const alias of validAliases) {
        try {
          await equivalencesService.create({
            principal_product_id: principal.id,
            alias_product_id: alias.id,
            sales_conversion_factor: salesFactor,
            stock_conversion_factor: stockFactor,
          });
          ok += 1;
        } catch (e) {
          errors.push(`${alias.code}: ${errorMessage(e)}`);
        }
      }
      if (ok > 0 && errors.length === 0) {
        setMessage(t('equivalences.created'));
      } else if (ok > 0) {
        setMessage(t('equivalences.createdPartial'));
        setError(errors.join(' · '));
      } else {
        setError(errors.join(' · ') || t('equivalences.selectBothProducts'));
      }
      setAliases([null]);
      setSalesFactor('1');
      setStockFactor('1');
      await reload();
    } finally {
      setSaving(false);
    }
  };

  const setScope = async (scope: string) => {
    if (!selected) return;
    await equivalencesService.patch(selected.canonical_product_id, { suggestion_scope: scope });
    await reload();
  };

  // CFG-EQV-004: por ahora solo «Solo principal»; normaliza grupos que quedaron en show_all.
  useEffect(() => {
    if (!selected || selected.suggestion_scope !== 'show_all') return;
    void setScope('principal_only');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.canonical_product_id, selected?.suggestion_scope]);

  const saveMemberUnits = async (
    memberId: number,
    patch: {
      sales_conversion_factor?: string;
      stock_conversion_factor?: string;
      sales_unit_id?: number | '';
      stock_unit_id?: number | '';
    }
  ) => {
    if (!selected) return;
    setError(null);
    try {
      await equivalencesService.patch(selected.canonical_product_id, {
        member_id: memberId,
        ...patch,
      });
      setMessage(t('equivalences.memberSaved'));
      await reload();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const dissolveGroup = async (revertUnification = false) => {
    if (!selected) return;
    const confirmKey = revertUnification
      ? 'equivalences.dissolveRevertConfirm'
      : 'equivalences.dissolveConfirm';
    if (!window.confirm(t(confirmKey))) return;
    setError(null);
    setMessage(null);
    setDissolving(revertUnification ? 'revert' : 'plain');
    try {
      await equivalencesService.dissolve(selected.canonical_product_id, {
        revertUnification,
      });
      setSelected(null);
      setSalesPreview(null);
      setStockPreview(null);
      setDissolving(null);
      setMessage(
        revertUnification
          ? t('equivalences.dissolvedReverted')
          : t('equivalences.dissolved')
      );
      await reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setDissolving(null);
    }
  };

  const removeMember = async (memberId: number, code: string) => {
    if (!selected) return;
    if (!window.confirm(t('equivalences.removeMemberConfirm', { code }))) return;
    setError(null);
    setMessage(null);
    try {
      const result = await equivalencesService.removeMember(
        selected.canonical_product_id,
        memberId
      );
      setSalesPreview(null);
      setStockPreview(null);
      if (result.group_dissolved) {
        setSelected(null);
        setMessage(t('equivalences.removeMemberDissolved'));
      } else {
        setMessage(t('equivalences.removeMemberDone', { code }));
      }
      await reload();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const runPreview = async (scope: 'sales' | 'stock') => {
    if (!selected) return;
    setError(null);
    setPreviewLoading(scope);
    try {
      const data = await equivalencesService.previewUnification(
        selected.canonical_product_id,
        scope
      );
      if (scope === 'sales') {
        setSalesPreview(data);
        setExpandedSalesDates(new Set());
      } else {
        setStockPreview(data);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setPreviewLoading(null);
    }
  };

  const runUnify = async (scope: 'sales' | 'stock') => {
    const preview = scope === 'sales' ? salesPreview : stockPreview;
    if (!selected || !preview?.preview_token) return;
    setError(null);
    setMessage(null);
    setUnifying(scope);
    try {
      await equivalencesService.runUnification(
        selected.canonical_product_id,
        String(preview.preview_token),
        scope
      );
      setMessage(t('equivalences.unified'));
      if (scope === 'sales') setSalesPreview(null);
      else setStockPreview(null);
      // Clear loading before reload so a slow list refresh does not leave "Guardando…".
      setUnifying(null);
      await reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setUnifying(null);
    }
  };

  const undoUnify = async (scope: 'sales' | 'stock') => {
    if (!selected) return;
    if (!window.confirm(t('equivalences.undoUnificationConfirm'))) return;
    setError(null);
    setMessage(null);
    setUndoing(scope);
    try {
      await equivalencesService.undoUnification(selected.canonical_product_id, scope);
      setMessage(t('equivalences.undoUnificationDone'));
      setSalesPreview(null);
      setStockPreview(null);
      setUndoing(null);
      await reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setUndoing(null);
    }
  };

  const excludeIds = [
    principal?.id,
    ...aliases.map((a) => a?.id),
  ].filter((x): x is number => typeof x === 'number');

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Link to="/settings" className={btnGhostClass}>
          <ArrowLeft className="h-4 w-4" />
          {t('productMaster.backToSettings')}
        </Link>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}
      {message ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {message}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Link2 className="h-5 w-5 text-stocker-primary" />
              {t('equivalences.groups')}
            </CardTitle>
            <CardDescription>
              {groups.length} {t('equivalences.groupsCount')}
            </CardDescription>
          </CardHeader>
          <CardContent className="!p-0">
            {groups.length === 0 ? (
              <p className="px-6 pb-6 text-sm text-gray-500">{t('equivalences.empty')}</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {groupsPageRows.map((g) => {
                  const active = selected?.canonical_product_id === g.canonical_product_id;
                  const principalRow = g.members.find((m) => m.is_active);
                  const gSales = g.sales_unification_status === 'done';
                  const gStock = g.stock_unification_status === 'done';
                  return (
                    <li key={g.canonical_product_id}>
                      <button
                        type="button"
                        className={`flex w-full items-center justify-between gap-3 px-6 py-3 text-left text-sm transition-colors ${
                          active ? 'bg-stocker-primaryLight/50' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => {
                          setSelected(g);
                          setSalesPreview(null);
                          setStockPreview(null);
                        }}
                      >
                        <span className="min-w-0">
                          <span className="block font-mono font-medium text-gray-900">
                            {g.canonical_code || g.canonical_product_id}
                          </span>
                          {principalRow?.equivalent_name ? (
                            <span className="block truncate text-xs text-gray-500">
                              {principalRow.equivalent_name}
                            </span>
                          ) : null}
                        </span>
                        <span className="flex shrink-0 flex-col items-end gap-1">
                          {g.conversion_pending ? (
                            <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                              {t('equivalences.conversionPending')}
                            </span>
                          ) : null}
                          {gSales ? (
                            <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-inset ring-emerald-200">
                              {t('equivalences.unifiedSalesBadge')}
                            </span>
                          ) : null}
                          {gStock ? (
                            <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-inset ring-emerald-200">
                              {t('equivalences.unifiedStockBadge')}
                            </span>
                          ) : null}
                          {!g.conversion_pending && !gSales && !gStock ? (
                            <span className="rounded-full bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
                              {t('equivalences.scopePrincipalOnly')}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {groups.length > 0 ? (
              <div className="border-t border-gray-100 px-3 pb-3">
                <TablePaginationBar
                  currentPage={groupsPageSafe}
                  totalPages={groupsTotalPages}
                  totalItems={groups.length}
                  pageSize={groupsPageSize}
                  onPageChange={setGroupsPage}
                  onPageSizeChange={(size) => {
                    setGroupsPageSize(size);
                    setGroupsPage(1);
                  }}
                />
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Plus className="h-5 w-5 text-stocker-primary" />
              {t('equivalences.addMember')}
            </CardTitle>
            <CardDescription>{t('equivalences.addMemberHelp')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ProductCodePicker
              label={t('equivalences.principalProduct')}
              value={principal}
              onChange={setPrincipal}
              excludeIds={excludeIds.filter((id) => id !== principal?.id)}
            />
            <div className="space-y-3">
              {aliases.map((alias, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <ProductCodePicker
                      label={`${t('equivalences.aliasProduct')} ${aliases.length > 1 ? idx + 1 : ''}`}
                      value={alias}
                      onChange={(p) => {
                        setAliases((prev) => {
                          const next = [...prev];
                          next[idx] = p;
                          return next;
                        });
                      }}
                      excludeIds={excludeIds.filter((id) => id !== alias?.id)}
                    />
                  </div>
                  {aliases.length > 1 ? (
                    <button
                      type="button"
                      className="mt-7 rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-red-600"
                      title={t('equivalences.removeAliasRow')}
                      onClick={() => setAliases((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
              ))}
              <button
                type="button"
                className={`${btnSecondaryClass} gap-1.5`}
                onClick={() => setAliases((prev) => [...prev, null])}
              >
                <Plus className="h-4 w-4" />
                {t('equivalences.addAliasRow')}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm font-medium text-gray-700">
                {t('equivalences.salesFactor')}
                <input
                  className={`mt-1 ${inputClass}`}
                  value={salesFactor}
                  onChange={(e) => setSalesFactor(e.target.value)}
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                {t('equivalences.stockFactor')}
                <input
                  className={`mt-1 ${inputClass}`}
                  value={stockFactor}
                  onChange={(e) => setStockFactor(e.target.value)}
                />
              </label>
            </div>
            <p className="text-xs text-gray-500">{t('equivalences.factorsHelp')}</p>
            <button
              type="button"
              className={btnPrimaryClass}
              disabled={saving || !principal || !aliases.some((a) => a?.id)}
              onClick={createGroup}
            >
              {saving ? t('common.saving') : t('equivalences.create')}
            </button>
          </CardContent>
        </Card>
      </div>

      {selected ? (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
                  <span>
                    {t('equivalences.groupDetail')} ·{' '}
                    {selected.canonical_code || selected.canonical_product_id}
                  </span>
                  {bothDone ? (
                    <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-inset ring-emerald-200">
                      {t('equivalences.unifiedBadge')}
                    </span>
                  ) : null}
                </CardTitle>
                <CardDescription>
                  {t('equivalences.principal')}:{' '}
                  {principalMember
                    ? `${principalMember.equivalent_code}${
                        principalMember.equivalent_name ? ` · ${principalMember.equivalent_name}` : ''
                      }`
                    : selected.principal_product_id}
                </CardDescription>
              </div>
              <div className="max-w-sm space-y-2 text-right">
                <div className="flex flex-wrap justify-end gap-2">
                  {(salesDone || stockDone) && (
                    <button
                      type="button"
                      className={btnDangerClass}
                      disabled={dissolving !== null || undoing !== null || unifying !== null}
                      onClick={() => dissolveGroup(true)}
                    >
                      {dissolving === 'revert'
                        ? t('common.saving')
                        : t('equivalences.dissolveRevertGroup')}
                    </button>
                  )}
                  <button
                    type="button"
                    className={btnDangerClass}
                    disabled={dissolving !== null || undoing !== null || unifying !== null}
                    onClick={() => dissolveGroup(false)}
                  >
                    {dissolving === 'plain'
                      ? t('common.saving')
                      : t('equivalences.dissolveGroup')}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  {salesDone || stockDone
                    ? t('equivalences.dissolveRevertHelp')
                    : t('equivalences.dissolveHelp')}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">{t('equivalences.suggestionScope')}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={`${btnSecondaryClass} ring-2 ring-stocker-primary`}
                  disabled
                >
                  {t('equivalences.scopePrincipalOnly')}
                </button>
              </div>
              <p className="text-xs text-gray-500">{t('equivalences.scopePrincipalOnlyHelp')}</p>
            </div>

            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                      {t('productMaster.code')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                      {t('equivalences.productUnit')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                      {t('equivalences.salesUnit')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                      {t('equivalences.stockUnit')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                      {t('equivalences.salesFactor')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                      {t('equivalences.stockFactor')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                      {t('equivalences.conversionStatus')}
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">
                      {t('equivalences.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {selectedMembers.map((m) => (
                    <tr key={m.id}>
                      <td className="px-3 py-2 font-mono">
                        {m.equivalent_code}
                        {m.is_active ? (
                          <span className="ml-2 text-xs text-stocker-primary">
                            ({t('equivalences.principal')})
                          </span>
                        ) : null}
                        <div className="max-w-[10rem] truncate text-xs font-sans text-gray-500">
                          {m.equivalent_name || '—'}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {m.sell_unit_abbreviation || m.sell_unit_name || '—'}
                        {' / '}
                        {m.buy_unit_abbreviation || m.buy_unit_name || '—'}
                      </td>
                      <td className="px-3 py-2">
                        <select
                          className={inputClass}
                          value={m.sales_unit_id ?? m.sell_unit_id ?? ''}
                          onChange={(e) =>
                            saveMemberUnits(m.id, {
                              sales_unit_id: e.target.value ? Number(e.target.value) : '',
                            })
                          }
                        >
                          <option value="">{t('equivalences.productUnit')}</option>
                          {units.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                              {u.abbreviation ? ` (${u.abbreviation})` : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          className={inputClass}
                          value={m.stock_unit_id ?? m.buy_unit_id ?? ''}
                          onChange={(e) =>
                            saveMemberUnits(m.id, {
                              stock_unit_id: e.target.value ? Number(e.target.value) : '',
                            })
                          }
                        >
                          <option value="">{t('equivalences.productUnit')}</option>
                          {units.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                              {u.abbreviation ? ` (${u.abbreviation})` : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          className={inputClass}
                          key={`sf-${m.id}-${m.sales_conversion_factor}`}
                          defaultValue={formatFactor(m.sales_conversion_factor)}
                          step="0.01"
                          onBlur={(e) => {
                            const next = formatFactor(e.target.value);
                            e.target.value = next;
                            if (next !== formatFactor(m.sales_conversion_factor)) {
                              saveMemberUnits(m.id, {
                                sales_conversion_factor: next,
                                stock_conversion_factor: formatFactor(m.stock_conversion_factor),
                              });
                            }
                          }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          className={inputClass}
                          key={`stf-${m.id}-${m.stock_conversion_factor}`}
                          defaultValue={formatFactor(m.stock_conversion_factor)}
                          step="0.01"
                          onBlur={(e) => {
                            const next = formatFactor(e.target.value);
                            e.target.value = next;
                            if (next !== formatFactor(m.stock_conversion_factor)) {
                              saveMemberUnits(m.id, {
                                sales_conversion_factor: formatFactor(m.sales_conversion_factor),
                                stock_conversion_factor: next,
                              });
                            }
                          }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        {m.conversion_pending ? (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800 ring-1 ring-amber-200">
                            {t('equivalences.conversionPending')}
                          </span>
                        ) : (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800 ring-1 ring-emerald-200">
                            {t('equivalences.conversionOk')}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {m.is_active ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                            title={t('equivalences.removeMember')}
                            onClick={() => removeMember(m.id, m.equivalent_code)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            {t('equivalences.removeMember')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-4 border-t border-gray-100 pt-4">
              <div>
                <p className="text-sm font-medium text-gray-700">{t('equivalences.unificationTitle')}</p>
                <p className="mt-1 text-xs text-gray-500">{t('equivalences.unificationHelp')}</p>
              </div>

              {/* Sales block */}
              <div className="space-y-3 rounded-md border border-gray-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-800">
                    {t('equivalences.unifySalesTitle')}
                    {salesDone ? (
                      <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-emerald-200">
                        {t('equivalences.unifiedSalesBadge')}
                      </span>
                    ) : null}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={btnSecondaryClass}
                      disabled={previewLoading !== null || unifying !== null || undoing !== null}
                      onClick={() => runPreview('sales')}
                    >
                      {previewLoading === 'sales'
                        ? t('common.loading')
                        : t('equivalences.previewSalesBtn')}
                    </button>
                    <button
                      type="button"
                      className={btnPrimaryClass}
                      disabled={
                        !salesPreview?.preview_token ||
                        unifying !== null ||
                        undoing !== null ||
                        salesDone
                      }
                      onClick={() => runUnify('sales')}
                    >
                      {unifying === 'sales'
                        ? t('common.saving')
                        : t('equivalences.confirmUnifySales')}
                    </button>
                    {salesDone ? (
                      <button
                        type="button"
                        className={btnDangerClass}
                        disabled={undoing !== null || unifying !== null}
                        onClick={() => undoUnify('sales')}
                      >
                        {undoing === 'sales'
                          ? t('common.saving')
                          : t('equivalences.undoUnificationSales')}
                      </button>
                    ) : null}
                  </div>
                </div>
                {salesPreview ? (
                  <div className="space-y-2 rounded-md border border-gray-100 bg-gray-50 p-3">
                    {salesPreview.summary?.nothing_to_move ? (
                      <p className="text-sm text-gray-700">{t('equivalences.previewEmpty')}</p>
                    ) : (
                      <>
                        <p className="text-sm text-gray-700">
                          {t('equivalences.previewSummarySales', {
                            sales:
                              salesPreview.summary?.sales_samples ??
                              salesPreview.sales_preview.length,
                            principal:
                              salesPreview.principal_code ||
                              principalMember?.equivalent_code ||
                              salesPreview.principal_product_id,
                          })}
                        </p>
                        <p className="text-xs text-gray-500">{t('equivalences.previewSalesHint')}</p>
                        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                          <table className="min-w-full divide-y divide-gray-200 bg-white">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="w-10 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" />
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  {t('equivalences.previewDate')}
                                </th>
                                <th className="w-36 px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                                  {t('equivalences.previewProducts')}
                                </th>
                                <th className="w-48 px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                                  {t('equivalences.previewQtyTotal')}
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {salesByDatePage.map((group, index) => {
                                const open = expandedSalesDates.has(group.date);
                                const rowBg =
                                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50';
                                return (
                                  <Fragment key={group.date}>
                                    <tr
                                      className={`cursor-pointer ${rowBg} hover:bg-blue-50/50`}
                                      onClick={() => toggleSalesDate(group.date)}
                                    >
                                      <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                                        {open ? (
                                          <ChevronDown className="h-4 w-4" />
                                        ) : (
                                          <ChevronRight className="h-4 w-4" />
                                        )}
                                      </td>
                                      <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-gray-900">
                                        {group.date}
                                      </td>
                                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                                        {group.productCount}
                                      </td>
                                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-gray-900">
                                        {formatQty(group.totalPrincipal)}
                                      </td>
                                    </tr>
                                    {open ? (
                                      <tr className="bg-blue-50/30">
                                        <td colSpan={4} className="px-4 py-3">
                                          <div className="overflow-x-auto rounded-lg border border-blue-200 bg-white">
                                            <table className="min-w-full divide-y divide-gray-200">
                                              <thead className="bg-gray-100">
                                                <tr>
                                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    {t('productMaster.code')}
                                                  </th>
                                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    {t('equivalences.previewWarehouse')}
                                                  </th>
                                                  <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    {t('equivalences.previewQtyAlias')}
                                                  </th>
                                                  <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    {t('equivalences.previewQtyPrincipal')}
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-gray-200 bg-white">
                                                {group.rows.map((row, idx) => (
                                                  <tr
                                                    key={`${group.date}-${row.alias_code}-${idx}`}
                                                    className={
                                                      idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                                    }
                                                  >
                                                    <td className="whitespace-nowrap px-4 py-2 font-mono text-sm font-semibold text-gray-900">
                                                      <span className="inline-flex items-center gap-2">
                                                        {row.alias_code}
                                                        {row.is_principal ? (
                                                          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-800">
                                                            {t('equivalences.principal')}
                                                          </span>
                                                        ) : null}
                                                      </span>
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-600">
                                                      {row.warehouse_id != null
                                                        ? String(row.warehouse_id)
                                                        : '—'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-700">
                                                      {formatQty(row.qty_alias)}
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-2 text-right text-sm font-semibold text-gray-900">
                                                      {formatQty(row.qty_principal_ceiled)}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </td>
                                      </tr>
                                    ) : null}
                                  </Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <TablePaginationBar
                          currentPage={salesDatesPageSafe}
                          totalPages={salesDatesTotalPages}
                          totalItems={salesByDate.length}
                          pageSize={salesDatesPageSize}
                          onPageChange={setSalesDatesPage}
                          onPageSizeChange={setSalesDatesPageSize}
                        />
                      </>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Stock block */}
              <div className="space-y-3 rounded-md border border-gray-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-800">
                    {t('equivalences.unifyStockTitle')}
                    {stockDone ? (
                      <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-emerald-200">
                        {t('equivalences.unifiedStockBadge')}
                      </span>
                    ) : null}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={btnSecondaryClass}
                      disabled={previewLoading !== null || unifying !== null || undoing !== null}
                      onClick={() => runPreview('stock')}
                    >
                      {previewLoading === 'stock'
                        ? t('common.loading')
                        : t('equivalences.previewStockBtn')}
                    </button>
                    <button
                      type="button"
                      className={btnPrimaryClass}
                      disabled={
                        !stockPreview?.preview_token ||
                        unifying !== null ||
                        undoing !== null ||
                        stockDone
                      }
                      onClick={() => runUnify('stock')}
                    >
                      {unifying === 'stock'
                        ? t('common.saving')
                        : t('equivalences.confirmUnifyStock')}
                    </button>
                    {stockDone ? (
                      <button
                        type="button"
                        className={btnDangerClass}
                        disabled={undoing !== null || unifying !== null}
                        onClick={() => undoUnify('stock')}
                      >
                        {undoing === 'stock'
                          ? t('common.saving')
                          : t('equivalences.undoUnificationStock')}
                      </button>
                    ) : null}
                  </div>
                </div>
                {stockPreview ? (
                  <div className="space-y-2 rounded-md border border-gray-100 bg-gray-50 p-3">
                    {stockPreview.summary?.nothing_to_move ? (
                      <p className="text-sm text-gray-700">{t('equivalences.previewEmpty')}</p>
                    ) : (
                      <>
                        <p className="text-sm text-gray-700">
                          {t('equivalences.previewSummaryStock', {
                            stock:
                              stockPreview.summary?.stock_lines ??
                              stockPreviewRows.rows.filter((r) => !r.is_principal).length,
                            principal:
                              stockPreview.principal_code ||
                              principalMember?.equivalent_code ||
                              stockPreview.principal_product_id,
                          })}
                        </p>
                        <p className="text-xs text-gray-500">{t('equivalences.previewStockHint')}</p>
                        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                          <table className="min-w-full divide-y divide-gray-200 bg-white">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  {t('productMaster.code')}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  {t('equivalences.previewWarehouse')}
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                                  {t('equivalences.previewQtyAlias')}
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                                  {t('equivalences.previewQtyTotal')}
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {stockPreviewRows.rows.map((row, idx) => (
                                <tr
                                  key={`st-${idx}`}
                                  className={
                                    row.is_principal
                                      ? 'bg-blue-50/60'
                                      : idx % 2 === 0
                                        ? 'bg-white hover:bg-blue-50/50'
                                        : 'bg-gray-50 hover:bg-blue-50/50'
                                  }
                                >
                                  <td className="whitespace-nowrap px-4 py-3 font-mono text-sm font-semibold text-gray-900">
                                    <span className="inline-flex items-center gap-2">
                                      {row.alias_code}
                                      {row.is_principal ? (
                                        <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-800">
                                          {t('equivalences.principal')}
                                        </span>
                                      ) : null}
                                    </span>
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                                    {row.warehouse_id ?? '—'}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                                    {formatQty(row.qty_alias)}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-gray-900">
                                    {formatQty(row.qty_principal_ceiled)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="border-t-2 border-gray-300 bg-gray-100">
                                <td
                                  colSpan={3}
                                  className="px-4 py-3 text-right text-sm font-semibold text-gray-800"
                                >
                                  {t('equivalences.previewStockTotalAfter')}
                                </td>
                                <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-bold text-gray-900">
                                  {formatQty(stockPreviewRows.totalAfter)}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
